import { useState, useRef } from "react";
import { ticketService } from "@/services/ticketService";
import { attachmentService } from "@/services/attachmentService";
import { formatError } from "@/services/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ROLE_LABELS } from "@/utils/constants";
import { formatDateTime } from "@/utils/formatters";
import { toast } from "sonner";
import { Send, Paperclip, X } from "lucide-react";

const STATUS_LABELS = { open: "Open", assigned: "Assigned", in_progress: "In Progress", on_hold: "On Hold", resolved: "Resolved", closed: "Closed", reopened: "Reopened" };

function getSubmitLabel(submitting, hasStatusChange) {
  if (submitting) return "Saving...";
  if (hasStatusChange) return "Update Status & Comment";
  return "Comment";
}

export function CommentsSection({ comments, setComments, setAttachments, canModify, user, ticketId, editStatus, setEditStatus, setTicket, refreshActivity }) {
  const [commentText, setCommentText] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [commentFiles, setCommentFiles] = useState([]);
  const commentFileRef = useRef(null);

  const hasStatusChange = Boolean(editStatus);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const valid = files.filter((f) => f.size <= 10 * 1024 * 1024);
    if (valid.length < files.length) toast.error("Some files exceed 10 MB limit");
    setCommentFiles((prev) => [...prev, ...valid]);
    if (commentFileRef.current) commentFileRef.current.value = "";
  };

  const removeFile = (idx) => setCommentFiles((prev) => prev.filter((_, i) => i !== idx));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) {
      if (hasStatusChange) { toast.error("A comment is required when changing status"); return; }
      return;
    }
    setSubmitting(true);
    try {
      if (hasStatusChange) {
        const { data } = await ticketService.updateStatus(ticketId, editStatus, commentText.trim());
        setTicket(data);
        setEditStatus(null);
      } else {
        const { data } = await ticketService.addComment(ticketId, { content: commentText, is_internal: isInternal });
        setComments((prev) => [...prev, data]);
      }

      for (const file of commentFiles) {
        try {
          const { data: att } = await attachmentService.upload(ticketId, file);
          setAttachments((prev) => [att, ...prev]);
        } catch { toast.error(`Failed to upload ${file.name}`); }
      }

      if (hasStatusChange) {
        const cRes = await ticketService.getComments(ticketId);
        setComments(cRes.data);
      }
      await refreshActivity();

      setCommentText(""); setIsInternal(false); setCommentFiles([]);
      toast.success(hasStatusChange ? "Status changed & comment added" : "Comment added");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); } finally { setSubmitting(false); }
  };

  return (
    <>
      {comments.length === 0 && <p className="text-sm text-[#52525B] py-4 text-center" data-testid="no-comments">No comments yet</p>}
      {comments.map((c) => <CommentCard key={c.id} comment={c} />)}
      {canModify && (
        <form onSubmit={handleSubmit} className="space-y-2" data-testid="comment-form">
          {hasStatusChange && (
            <div className="flex items-center gap-2 px-3 py-2 bg-violet-50 border border-violet-200 rounded-sm" data-testid="status-change-banner">
              <span className="text-xs font-medium text-violet-700">Changing status to <span className="capitalize font-bold">{STATUS_LABELS[editStatus]}</span> — comment required</span>
              <button type="button" onClick={() => setEditStatus(null)} className="ml-auto text-violet-500 hover:text-violet-700"><X size={14} /></button>
            </div>
          )}
          <Textarea data-testid="comment-input" value={commentText} onChange={(e) => setCommentText(e.target.value)}
            placeholder={hasStatusChange ? "Explain the reason for this status change..." : "Add a comment..."}
            className={`rounded-sm min-h-[80px] text-sm ${hasStatusChange ? "border-violet-300 focus:border-violet-500" : "border-zinc-300"}`} />

          {commentFiles.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="comment-files-list">
              {commentFiles.map((f, i) => (
                <span key={`${f.name}-${f.size}`} className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-[#F4F4F5] rounded-sm" data-testid={`comment-file-${i}`}>
                  <Paperclip size={11} /> {f.name}
                  <button type="button" onClick={() => removeFile(i)} className="text-[#52525B] hover:text-red-500"><X size={11} /></button>
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {user?.role !== "end_user" && !hasStatusChange && (<><Checkbox id="internal" checked={isInternal} onCheckedChange={setIsInternal} data-testid="internal-note-checkbox" /><Label htmlFor="internal" className="text-xs text-[#52525B]">Internal note</Label></>)}
              <input ref={commentFileRef} type="file" className="hidden" onChange={handleFileSelect} multiple data-testid="comment-file-input" />
              <Button variant="ghost" size="sm" type="button" onClick={() => commentFileRef.current?.click()} className="h-7 rounded-sm text-xs gap-1 text-[#52525B]" data-testid="attach-file-btn">
                <Paperclip size={13} /> Attach
              </Button>
            </div>
            <Button type="submit" disabled={submitting || !commentText.trim()} data-testid="submit-comment-btn"
              className={`rounded-sm text-white gap-1.5 h-8 ${hasStatusChange ? "bg-violet-600 hover:bg-violet-700" : "bg-[#003DA5] hover:bg-[#002D7A]"}`}>
              <Send size={13} /> {getSubmitLabel(submitting, hasStatusChange)}
            </Button>
          </div>
        </form>
      )}
    </>
  );
}

function CommentCard({ comment: c }) {
  let cardBorder = "border-[#E4E4E7]";
  if (c.is_status_change) cardBorder = "border-violet-200 bg-violet-50/50";
  else if (c.is_internal) cardBorder = "border-amber-200 bg-amber-50/50";

  return (
    <Card className={`border shadow-none rounded-sm ${cardBorder}`} data-testid={`comment-${c.id}`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-sm font-medium text-[#09090B]">{c.user_name}</span>
          <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-[#52525B]">{ROLE_LABELS[c.user_role]}</span>
          {c.is_status_change && <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-sm">{c.status_from} → {c.status_to}</span>}
          {c.is_internal && <span className="text-[10px] uppercase tracking-[0.1em] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm">Internal</span>}
          <span className="text-xs text-[#52525B] ml-auto">{formatDateTime(c.created_at)}</span>
        </div>
        <p className="text-sm text-[#09090B] whitespace-pre-wrap">{c.content}</p>
      </CardContent>
    </Card>
  );
}
